import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Modal,
  Typography,
  IconButton,
  InputAdornment,
  CircularProgress,
  Grid,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faFile,
  faFolderOpen,
  faLock,
  faUnlock,
  faTrash,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import {
  getFileType,
} from "../../apis/agent";
import {
  getCompanyDevices,
  editLastLogin,
  addQueueOperation,
  addOperation,
  checkValid,
  requestOperation,
} from "../../apis/axios";
import AuthContext from "../../AuthContext";
import ConfirmDialog from "../common/ConfirmationModal";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { useTheme } from "@mui/material";

const FileInsightsCard = ({
  uuid,
  sys_uuid,
  drive,
  pathCurrent,
  refreshTrigger,
  device_id,
  licence_id,
}) => {
  const theme = useTheme();
  const auth = useContext(AuthContext);
  const [fileTypeStats, setFileTypeStats] = useState([]);
  const [initialFileTypeStats, setInitialFileTypeStats] = useState([]);
  const [sortOption, setSortOption] = useState("By Name");
  const [filterOption, setFilterOption] = useState("All Formats");
  const [searchQuery, setSearchQuery] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");
  const [error, setError] = useState(null);
  const [encryptionStatus, setEncryptionStatus] = useState({});
  const [openModal, setOpenModal] = useState(false);
  const [modalMode, setModalMode] = useState("encrypt");
  const [loading, setLoading] = useState(false);
  const [modalFormat, setModalFormat] = useState("");
  const [password, setPassword] = useState("");
  const [loadingFormats, setLoadingFormats] = useState({});
  const [openEncryptConfirm, setOpenEncryptConfirm] = useState(false);
  const [openDecryptConfirm, setOpenDecryptConfirm] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [formatToDelete, setFormatToDelete] = useState("");
  const [pendingFormat, setPendingFormat] = useState("");
  const [pendingPassword, setPendingPassword] = useState("");
  const [openRequest, setOpenRequest] = useState(false);

  const extractDriveFromPath = (path) => {
    if (!path) return null;
    const match = path.match(/^([A-Z]):[\/\\]/);
    if (match) {
      const driveLetter = match[1];
      return `${driveLetter}:\\`;
    }
    return null;
  };

  const formatStorageSize = (bytes) => {
    if (bytes === "Unknown" || bytes == null) return "Unknown";
    const kb = bytes / 1024;
    const mb = kb / 1024;
    const gb = mb / 1024;
    const formatNumber = (num) => {
      const integerPart = Math.floor(num);
      const integerDigits = integerPart.toString().length;
      let formatted;
      if (integerDigits >= 3) {
        formatted = num.toFixed(1);
      } else if (integerDigits === 2) {
        formatted = num.toFixed(2);
      } else {
        formatted = num.toFixed(3);
      }
      return Number(formatted).toString();
    };
    if (gb >= 1) {
      return `${formatNumber(gb)} GB`;
    } else if (mb >= 1) {
      const mbIntegerPart = Math.floor(mb);
      if (mbIntegerPart.toString().length >= 4) {
        return `${formatNumber(gb)} GB`;
      }
      return `${formatNumber(mb)} MB`;
    } else {
      const kbIntegerPart = Math.floor(kb);
      if (kbIntegerPart.toString().length >= 4) {
        return `${formatNumber(mb)} MB`;
      }
      return `${formatNumber(kb)} KB`;
    }
  };

  const parseSizeToBytes = (sizeStr) => {
    const units = { KB: 1024, MB: 1024 * 1024, GB: 1024 * 1024 * 1024 };
    const [value, unit] = sizeStr.split(/(\d+)/).filter(Boolean);
    return parseFloat(value) * (units[unit.toUpperCase()] || 1);
  };

  const checkDeviceStatus = async () => {
    try {
      const companyId = auth.admin || "";
      if (!companyId) {
        throw new Error("companyId is empty or undefined.");
      }
      if (!uuid) {
        throw new Error("uuid is empty or undefined.");
      }

      const deviceResponse = await getCompanyDevices({ company_id: companyId });

      if (deviceResponse && Array.isArray(deviceResponse.data)) {
        const device = deviceResponse.data.find((dev) => dev.uuid === uuid);
        if (!device) {
          throw new Error("Device not found.");
        }
        if (!device.id) {
          throw new Error("Device found but id is missing.");
        }

        const deviceId = device.id;
        const payload = {
          id: deviceId,
          uuid: uuid,
        };
        const statusResponse = await editLastLogin(payload);
        const isActive = statusResponse.data.isActive === 1;

        return { isActive, deviceId };
      } else {
        throw new Error("Invalid response from getCompanyDevices.");
      }
    } catch (err) {
      setError(
        "Failed to verify device status. Please ensure the device is registered and try again."
      );
      return { isActive: false, deviceId: null };
    }
  };

  const checkLicence = async () => {
    try {
      const res = await checkValid({ licence_id });
      if (res.data.status === "limit_exceeded") {
        setSnackbarMsg("Licence limit exceeded");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      } else {
        setOpenDeleteConfirm(true);
      }
    } catch (error) {
      console.error(error);
      setSnackbarMsg(error?.response?.data?.error || "Something went wrong");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const fetchFileTypes = async () => {
    setLoading(true);
    const derivedDrive = drive || extractDriveFromPath(pathCurrent);
    if (!derivedDrive) {
      setError(
        "Unable to determine drive. Please provide a valid drive or path."
      );
      return;
    }

    try {
      const response = await getFileType(uuid, derivedDrive, sys_uuid);
      const mappedStats = response.data.file_types.map((item) => {
        const type =
          item.extension === "[no extension]"
            ? "No Extension"
            : item.extension.startsWith(".")
            ? item.extension.slice(1).toUpperCase()
            : item.extension.toUpperCase();
        return {
          type: type,
          count: item.count,
          totalSize: formatStorageSize(item.size),
          totalBytes: item.size,
        };
      });

      if (initialFileTypeStats.length === 0) {
        setInitialFileTypeStats(mappedStats);
      }

      const mergedStats = initialFileTypeStats.map((initialStat) => {
        const updatedStat =
          mappedStats.find((stat) => stat.type === initialStat.type) || {
            type: initialStat.type,
            count: 0,
            totalSize: formatStorageSize(0),
            totalBytes: 0,
          };
        return {
          ...initialStat,
          count: updatedStat.count,
          totalSize: updatedStat.totalSize,
          totalBytes: updatedStat.totalBytes,
        };
      });

      mappedStats.forEach((stat) => {
        if (!initialFileTypeStats.some((initial) => initial.type === stat.type)) {
          mergedStats.push(stat);
        }
      });

      setFileTypeStats(mergedStats);

      setEncryptionStatus((prev) => {
        const updatedStatus = { ...prev };
        mergedStats.forEach((stat) => {
          const isEncrypted = stat.type.toUpperCase().endsWith(".ENC");
          updatedStatus[stat.type] = isEncrypted;
        });
        return updatedStatus;
      });
    } catch (err) {
      setError("Failed to load file type insights. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFileTypes();
  }, [pathCurrent, uuid, drive, refreshTrigger]);

  const openModalHandler = (mode, format) => {
    setModalMode(mode);
    setModalFormat(format);
    setOpenModal(true);
  };

  const closeModal = () => {
    setOpenModal(false);
    setPassword("");
  };

  const handleEncryptFormat = () => {
    if (!password) {
      setError("Encryption password is required.");
      return;
    }
    setPendingFormat(modalFormat);
    setPendingPassword(password);
    setOpenModal(false);
    setOpenEncryptConfirm(true);
  };

  const handleConfirmEncrypt = async () => {
    try {
      setLoadingFormats((prev) => ({ ...prev, [pendingFormat]: true }));
      setError(null);
      setOpenEncryptConfirm(false);

      const derivedDrive = drive || extractDriveFromPath(pathCurrent);
      if (!derivedDrive) {
        setError(
          "Unable to determine drive. Please provide a valid drive or path."
        );
        return;
      }

      if (!pendingFormat || !pendingPassword) {
        setError("File format or password is missing. Please try again.");
        return;
      }

      const { isActive } = await checkDeviceStatus();

      if (isActive) {
        const operationData = {
          device_id: device_id,
          user_id: auth.user,
          type: "encrypt_file_format",
          json: {
            path: derivedDrive,
            format: pendingFormat.toLowerCase(),
            password: pendingPassword,
          },
        };
        const operationResponse = await requestOperation(operationData);
        setOpenRequest(true);
        const operationLogId = operationResponse.data.log_id;

        if (!operationLogId) {
          throw new Error("No log_id returned from addOperation");
        }

       

        setEncryptionStatus((prev) => ({
          ...prev,
          [pendingFormat]: true,
        }));
        await fetchFileTypes();
      } else {
        const userId = auth.user;
        if (!userId) {
          throw new Error("User ID not found. Please log in again.");
        }
        if (!device_id) {
          throw new Error("Device ID not found. Cannot queue operation.");
        }

        const operationData = {
          device_id: device_id,
          user_id: userId,
          type: "encrypt_file_format",
          json: {
            path: derivedDrive,
            format: pendingFormat.toLowerCase(),
            password: pendingPassword,
          },
        };
        const queueResponse = await requestOperation(operationData);
         setOpenRequest(true);
        const queueLogId = queueResponse.data.log_id;

        if (!queueLogId) {
          throw new Error("No log_id returned from addQueueOperation");
        }

        setError("Device is offline. Encryption operation has been queued.");
      }
    } catch (err) {
      setError(err.message || "Failed to process encryption. Please try again.");
    } finally {
      setLoadingFormats((prev) => ({ ...prev, [pendingFormat]: false }));
      setPendingFormat("");
      setPendingPassword("");
      setModalFormat("");
      setPassword("");
    }
  };

  const handleDecryptFormat = () => {
    if (!password) {
      setError("Decryption password is required.");
      return;
    }
    setPendingFormat(modalFormat);
    setPendingPassword(password);
    setOpenModal(false);
    setOpenDecryptConfirm(true);
  };

  const handleConfirmDecrypt = async () => {
    try {
      setLoadingFormats((prev) => ({ ...prev, [pendingFormat]: true }));
      setError(null);
      setOpenDecryptConfirm(false);

      const derivedDrive = drive || extractDriveFromPath(pathCurrent);
      if (!derivedDrive) {
        setError(
          "Unable to determine drive. Please provide a valid drive or path."
        );
        return;
      }

      if (!pendingFormat || !pendingPassword) {
        setError("File format or password is missing. Please try again.");
        return;
      }

      const { isActive } = await checkDeviceStatus();

      if (isActive) {
        const operationData = {
          device_id: device_id,
          user_id: auth.user,
          type: "decrypt_file_format",
          json: {
            path: derivedDrive,
            format: pendingFormat.toLowerCase(),
            password: pendingPassword,
          },
        };
        const operationResponse = await requestOperation(operationData);
        setOpenRequest(true);
        const operationLogId = operationResponse.data.log_id;

        if (!operationLogId) {
          throw new Error("No log_id returned from addOperation");
        }

        

      

        await fetchFileTypes();
      } else {
        const userId = auth.user;
        if (!userId) {
          throw new Error("User ID not found. Please log in again.");
        }
        if (!device_id) {
          throw new Error("Device ID not found. Cannot queue operation.");
        }

        const operationData = {
          device_id: device_id,
          user_id: userId,
          type: "decrypt_file_format",
          json: {
            path: derivedDrive,
            format: pendingFormat.toLowerCase(),
            password: pendingPassword,
          },
        };
        const queueResponse = await requestOperation(operationData);
        setOpenRequest(true);
        const queueLogId = queueResponse.data.log_id;

        if (!queueLogId) {
          throw new Error("No log_id returned from addQueueOperation");
        }

        setError("Device is offline. Decryption operation has been queued.");
      }
    } catch (err) {
      setError(err.message || "Failed to process decryption. Please try again.");
    } finally {
      setLoadingFormats((prev) => ({ ...prev, [pendingFormat]: false }));
      setPendingFormat("");
      setPendingPassword("");
      setModalFormat("");
      setPassword("");
    }
  };

  const handleDeleteFormat = (format) => {
    checkLicence();
    setFormatToDelete(format);
  };

  const handleConfirmDelete = async () => {
    try {
      setLoadingFormats((prev) => ({ ...prev, [formatToDelete]: true }));
      setError(null);
      setOpenDeleteConfirm(false);

      const derivedDrive = drive || extractDriveFromPath(pathCurrent);
      if (!derivedDrive) {
        setError(
          "Unable to determine drive. Please provide a valid drive or path."
        );
        return;
      }

      const { isActive } = await checkDeviceStatus();

      if (isActive) {
        const operationData = {
          device_id: device_id,
          user_id: auth.user,
          type: "delete_file_format",
          json: {
            path: derivedDrive,
            format: formatToDelete.toLowerCase(),
          },
        };
        const operationResponse = await requestOperation(operationData);
        setOpenRequest(true);
        const operationLogId = operationResponse.data.log_id;

        if (!operationLogId) {
          throw new Error("No log_id returned from addOperation");
        }

        await fetchFileTypes();
        setEncryptionStatus((prev) => {
          const newStatus = { ...prev };
          delete newStatus[formatToDelete];
          return newStatus;
        });
      } else {
        const userId = auth.user;
        if (!userId) {
          throw new Error("User ID not found. Please log in again.");
        }
        if (!device_id) {
          throw new Error("Device ID not found. Cannot queue operation.");
        }

        const operationData = {
          device_id: device_id,
          user_id: userId,
          type: "delete_file_format",
          json: {
            path: derivedDrive,
            format: formatToDelete.toLowerCase(),
          },
        };
        const queueResponse = await requestOperation(operationData);
        const queueLogId = queueResponse.data.log_id;

        if (!queueLogId) {
          throw new Error("No log_id returned from addQueueOperation");
        }

        setError("Device is offline. Deletion operation has been queued.");
      }
    } catch (err) {
      setError("Failed to delete selected format.");
    } finally {
      setLoadingFormats((prev) => ({ ...prev, [formatToDelete]: false }));
      setFormatToDelete("");
    }
  };

  const handleCancelEncrypt = () => {
    setOpenEncryptConfirm(false);
    setPendingFormat("");
    setPendingPassword("");
    setModalFormat("");
    setPassword("");
  };

  const handleCancelDecrypt = () => {
    setOpenDecryptConfirm(false);
    setPendingFormat("");
    setPendingPassword("");
    setModalFormat("");
    setPassword("");
  };

  const handleCancelDelete = () => {
    setOpenDeleteConfirm(false);
    setFormatToDelete("");
  };

  const filteredFileTypes = fileTypeStats
    .filter((stat) => {
      const matchesSearch = stat.type
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
      const matchesFilter =
        filterOption === "All Formats" ||
        (filterOption === "Encrypted Formats" &&
          encryptionStatus[stat.type]) ||
        (filterOption === "Decrypted Formats" &&
          !encryptionStatus[stat.type]);
      return matchesSearch && matchesFilter;
    })
    .sort((a, b) => {
      if (sortOption === "By Name") {
        return a.type.localeCompare(b.type);
      } else if (sortOption === "By Size") {
        return b.totalBytes - a.totalBytes;
      } else if (sortOption === "By Number of Files") {
        return b.count - a.count;
      }
      return 0;
    });

  if (!auth.isLoggedIn || !auth.user) {
    return (
      <Box
        sx={{
          bgcolor: "background.paper",
          p: { xs: 2, sm: 3, md: 4 },
          boxShadow: 3,
          borderRadius: 2,
          border: 1,
          borderColor: "grey.200",
          fontFamily: theme.typography.fontFamily,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 2,
            color: "text.primary",
          }}
        >
          <FontAwesomeIcon icon={faFolderOpen} color={theme.palette.primary.main} />
          File Insights & Actions
        </Typography>
        <Typography color="error.main" align="center" variant="body2">
          User not logged in. Please log in to access file insights.
        </Typography>
      </Box>
    );
  }

  if (error && fileTypeStats.length === 0) {
    return (
      <Box
        sx={{
          bgcolor: "background.paper",
          p: { xs: 2, sm: 3, md: 4 },
          boxShadow: 3,
          borderRadius: 2,
          border: 1,
          borderColor: "grey.200",
          fontFamily: theme.typography.fontFamily,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 2,
            color: "text.primary",
          }}
        >
          <FontAwesomeIcon icon={faFolderOpen} color={theme.palette.primary.main} />
          File Insights & Actions
        </Typography>
        <Typography color="error.main" align="center" variant="body2">
          {error}
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box
        sx={{
          bgcolor: "background.paper",
          p: { xs: 2, sm: 3, md: 4 },
          boxShadow: 3,
          borderRadius: 2,
          border: 1,
          borderColor: "grey.200",
          fontFamily: theme.typography.fontFamily,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 2,
            color: "text.primary",
          }}
        >
          <FontAwesomeIcon icon={faFolderOpen} color={theme.palette.primary.main} />
          File Insights & Actions
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        p: { xs: 2, sm: 3, md: 4 },
        boxShadow: 3,
        borderRadius: 2,
        border: 1,
        borderColor: "grey.200",
        fontFamily: theme.typography.fontFamily,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            color: "text.primary",
          }}
        >
          <FontAwesomeIcon icon={faFolderOpen} color={theme.palette.primary.main} />
          File Insights & Actions
        </Typography>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
          <TextField
            size="small"
            placeholder="Search File Types"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FontAwesomeIcon icon={faSearch} color={theme.palette.grey[400]} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: "100%", sm: 200 },
              "& .MuiInputBase-root": {
                height: 36,
                fontSize: "0.875rem",
                bgcolor: "grey.50",
                borderRadius: 1,
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "grey.300",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "primary.main",
              },
              "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "primary.main",
              },
            }}
          />
          <TextField
            select
            size="small"
            value={sortOption}
            onChange={(e) => setSortOption(e.target.value)}
            label="Sort By"
            sx={{
              width: { xs: "100%", sm: 160 },
              "& .MuiInputBase-root": {
                height: 36,
                fontSize: "0.875rem",
                bgcolor: "grey.50",
                borderRadius: 1,
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "grey.300",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "primary.main",
              },
              "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "primary.main",
              },
            }}
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  sx: {
                    "& .MuiMenuItem-root": {
                      fontSize: "0.875rem",
                      "&:hover": {
                        bgcolor: "action.hover",
                      },
                      "&.Mui-selected": {
                        bgcolor: "primary.main",
                        color: "common.white",
                        "&:hover": {
                          bgcolor: "primary.dark",
                        },
                      },
                    },
                  },
                },
              },
            }}
          >
            <MenuItem value="By Name">By Name</MenuItem>
            <MenuItem value="By Size">By Size</MenuItem>
            <MenuItem value="By Number of Files">By Number of Files</MenuItem>
          </TextField>
          <TextField
            select
            size="small"
            value={filterOption}
            onChange={(e) => setFilterOption(e.target.value)}
            label="Filter By"
            sx={{
              width: { xs: "100%", sm: 160 },
              "& .MuiInputBase-root": {
                height: 36,
                fontSize: "0.875rem",
                bgcolor: "grey.50",
                borderRadius: 1,
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "grey.300",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "primary.main",
              },
              "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "primary.main",
              },
            }}
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  sx: {
                    "& .MuiMenuItem-root": {
                      fontSize: "0.875rem",
                      "&:hover": {
                        bgcolor: "action.hover",
                      },
                      "&.Mui-selected": {
                        bgcolor: "primary.main",
                        color: "common.white",
                        "&:hover": {
                          bgcolor: "primary.dark",
                        },
                      },
                    },
                  },
                },
              },
            }}
          >
            <MenuItem value="All Formats">All Formats</MenuItem>
            <MenuItem value="Encrypted Formats">Encrypted Formats</MenuItem>
            <MenuItem value="Decrypted Formats">Decrypted Formats</MenuItem>
          </TextField>
        </Box>
      </Box>

      {fileTypeStats.length === 0 ? (
        <Typography color="text.secondary" align="center" variant="body2">
          No file types found on this drive.
        </Typography>
      ) : filteredFileTypes.length === 0 ? (
        <Typography color="text.secondary" align="center" variant="body2">
          No file types match your search or filter criteria.
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {filteredFileTypes.map((stat, index) => (
            <Grid item size={{ xs: 12, sm: 6, lg: 4 }} key={index}>
              <Box
                sx={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  p: 2,
                  border: 1,
                  borderColor: "grey.300",
                  borderRadius: 2,
                  bgcolor: "background.paper",
                  transition: "box-shadow 0.2s",
                  "&:hover": {
                    boxShadow: 2,
                  },
                  minWidth: 0, // Ensure the box doesn't overflow its parent
                }}
              >
                <FontAwesomeIcon
                  icon={faFile}
                  style={{ color: theme.palette.grey[600], marginRight: 12, flexShrink: 0 }}
                  size="lg"
                />
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0, // Prevent overflow
                    pr: 2, // Add padding to separate from buttons
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: "bold",
                      color: "text.primary",
                      mb: 0.5,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={stat.type} // Show full name on hover
                  >
                    {stat.type}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: "medium" }}>
                    {stat.count} {stat.count === 1 ? "file" : "files"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: "medium" }}>
                    Total Size: {stat.totalSize}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    flexShrink: 0, // Prevent buttons from shrinking
                    width: { xs: 120, sm: 140 }, // Fixed width for buttons
                    alignItems: "flex-end", // Align buttons to the right
                  }}
                >
                  <Button
                    variant={encryptionStatus[stat.type] ? "outlined" : "contained"}
                    color="primary"
                    startIcon={
                      <FontAwesomeIcon icon={encryptionStatus[stat.type] ? faUnlock : faLock} />
                    }
                    onClick={() =>
                      encryptionStatus[stat.type]
                        ? openModalHandler("decrypt", stat.type)
                        : openModalHandler("encrypt", stat.type)
                    }
                    disabled={loadingFormats[stat.type]}
                    sx={{
                      borderRadius: 1,
                      textTransform: "none",
                      fontSize: "0.875rem",
                      px: 2,
                      py: 0.5,
                      width: "100%", // Ensure button takes full width of container
                      minWidth: 0, // Allow button to fit within container
                    }}
                  >
                    {encryptionStatus[stat.type] ? "Decrypt All" : "Encrypt All"}
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<FontAwesomeIcon icon={faTrash} />}
                    onClick={() => handleDeleteFormat(stat.type)}
                    disabled={loadingFormats[stat.type]}
                    sx={{
                      borderRadius: 1,
                      textTransform: "none",
                      fontSize: "0.875rem",
                      px: 2,
                      py: 0.5,
                      width: "100%", // Ensure button takes full width of container
                      minWidth: 0, // Allow button to fit within container
                    }}
                  >
                    Delete All
                  </Button>
                </Box>
                {loadingFormats[stat.type] && (
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "grey.200",
                      opacity: 0.5,
                      borderRadius: 2,
                    }}
                  >
                    <CircularProgress size={24} />
                  </Box>
                )}
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      {error && fileTypeStats.length > 0 && (
        <Typography
          color="error.main"
          align="center"
          variant="body2"
          sx={{ mt: 2 }}
        >
          {error}
        </Typography>
      )}

      <Modal
        open={openModal}
        onClose={closeModal}
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <Box
          sx={{
            bgcolor: "background.paper",
            p: 3,
            borderRadius: 2,
            width: { xs: "90%", sm: 400 },
            maxWidth: 500,
            boxShadow: 24,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", color: "text.primary" }}>
              {modalMode === "encrypt" ? "Encryption Settings" : "Decryption Settings"}
            </Typography>
            <IconButton onClick={closeModal} size="small">
              <FontAwesomeIcon icon={faTimes} color={theme.palette.primary.main} />
            </IconButton>
          </Box>
          <Typography variant="body2" sx={{ fontWeight: "medium", mb: 1, color: "text.primary" }}>
            {modalMode === "encrypt" ? "Encryption Password*" : "Decryption Password*"}
          </Typography>
          <TextField
            fullWidth
            type="password"
            placeholder={
              modalMode === "encrypt"
                ? "Enter a strong password"
                : "Enter the decryption password"
            }
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            sx={{
              mb: 2,
              "& .MuiInputBase-root": {
                height: 40,
                fontSize: "0.875rem",
                bgcolor: "grey.50",
                borderRadius: 1,
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "grey.300",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "primary.main",
              },
              "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "primary.main",
              },
            }}
          />
          {password && (
            <Typography
              variant="caption"
              color="error.main"
              sx={{ display: "block", mb: 2 }}
            >
              ⚠️ {modalMode === "encrypt"
                ? "Please store your encryption password securely. If forgotten, the encrypted data cannot be recovered or decrypted."
                : "Ensure the password matches the one used for encryption."}
            </Typography>
          )}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button
              variant="outlined"
              onClick={closeModal}
              sx={{
                textTransform: "none",
                fontSize: "0.875rem",
                borderRadius: 1,
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={modalMode === "encrypt" ? handleEncryptFormat : handleDecryptFormat}
              sx={{
                textTransform: "none",
                fontSize: "0.875rem",
                borderRadius: 1,
              }}
            >
              Confirm
            </Button>
          </Box>
        </Box>
      </Modal>

      <ConfirmDialog
        open={openEncryptConfirm}
        title="Confirm Encryption"
        message={`Are you sure you want to encrypt all ${pendingFormat || modalFormat} files? This action cannot be undone without the password.`}
        onConfirm={handleConfirmEncrypt}
        onCancel={handleCancelEncrypt}
        confirmText="Encrypt"
        cancelText="Cancel"
        confirmButtonColor="primary"
      />

      <ConfirmDialog
        open={openDecryptConfirm}
        title="Confirm Decryption"
        message={`Are you sure you want to decrypt all ${pendingFormat || modalFormat} files? Ensure the password is correct.`}
        onConfirm={handleConfirmDecrypt}
        onCancel={handleCancelDecrypt}
        confirmText="Decrypt"
        cancelText="Cancel"
        confirmButtonColor="primary"
      />

      <ConfirmDialog
        open={openDeleteConfirm}
        title="Confirm Deletion"
        message={`Are you sure you want to delete all ${formatToDelete} files? This action cannot be undone.`}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonColor="error"
      />

      <ConfirmDialog
        open={openRequest}
        title="Request for Deletion"
        message="Deletion Request is sent to Admin, You Will Receive OTP Shortly"
        onConfirm={() => setOpenRequest(false)}
        onCancel={() => setOpenRequest(false)}
        confirmText="OK"
        cancelText="Cancel"
        confirmButtonColor="primary"
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FileInsightsCard;